import React from "react";
import { Card, Row, Col, Descriptions, Table, Divider, Tag } from "antd";

const TransactionDetails = ({ transaction }) => {
  // Extract transaction details from the transaction object
  const details = transaction?.transaction_details || {};
  
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Helper function to format amount
  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "";
    return `$${parseFloat(amount).toFixed(2)}`;
  };
  
  // Render payment information
  const renderPaymentInfo = () => {
    const payment = details.payment || {};
    const creditCard = payment.creditCard || {};
    
    return (
      <Descriptions title="Payment Information" bordered column={2}>
        <Descriptions.Item label="Card Number">{creditCard.cardNumber || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Card Type">{creditCard.cardType || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Expiration Date">{creditCard.expirationDate || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Recurring Billing">{details.recurringBilling === "true" ? "Yes" : "No"}</Descriptions.Item>
      </Descriptions>
    );
  };
  
  // Render customer information
  const renderCustomerInfo = () => {
    const customer = details.customer || {};
    const billTo = details.billTo || {};
    const shipTo = details.shipTo || {};
    
    return (
      <>
        <Descriptions title="Customer Information" bordered column={2}>
          <Descriptions.Item label="Customer ID">{customer.id || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Customer Type">{customer.type || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Email">{customer.email || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="IP Address">{details.customerIP || "N/A"}</Descriptions.Item>
        </Descriptions>
        
        <Divider />
        
        <Row gutter={16}>
          <Col span={12}>
            <Card title="Billing Address" bordered={false}>
              <p><strong>Name:</strong> {billTo.firstName || ""} {billTo.lastName || ""}</p>
              <p><strong>Address:</strong> {billTo.address || "N/A"}</p>
              <p><strong>City:</strong> {billTo.city || "N/A"}</p>
              <p><strong>State:</strong> {billTo.state || "N/A"}</p>
              <p><strong>Zip:</strong> {billTo.zip || "N/A"}</p>
              <p><strong>Country:</strong> {billTo.country || "N/A"}</p>
              <p><strong>Phone:</strong> {billTo.phoneNumber || "N/A"}</p>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Shipping Address" bordered={false}>
              <p><strong>Name:</strong> {shipTo.firstName || ""} {shipTo.lastName || ""}</p>
              <p><strong>Address:</strong> {shipTo.address || "N/A"}</p>
              <p><strong>City:</strong> {shipTo.city || "N/A"}</p>
              <p><strong>State:</strong> {shipTo.state || "N/A"}</p>
              <p><strong>Zip:</strong> {shipTo.zip || "N/A"}</p>
              <p><strong>Country:</strong> {shipTo.country || "N/A"}</p>
            </Card>
          </Col>
        </Row>
      </>
    );
  };
  
  // Render order information
  const renderOrderInfo = () => {
    const order = details.order || {};
    const lineItems = details.lineItems?.lineItem || [];
    
    const lineItemColumns = [
      {
        title: "Item ID",
        dataIndex: "itemId",
        key: "itemId",
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
      },
      {
        title: "Unit Price",
        dataIndex: "unitPrice",
        key: "unitPrice",
        render: (text) => formatAmount(text),
      },
      {
        title: "Taxable",
        dataIndex: "taxable",
        key: "taxable",
        render: (text) => text === "true" ? "Yes" : "No",
      },
    ];
    
    return (
      <>
        <Descriptions title="Order Information" bordered column={2}>
          <Descriptions.Item label="Invoice Number">{order.invoiceNumber || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="PO Number">{order.purchaseOrderNumber || "N/A"}</Descriptions.Item>
          <Descriptions.Item label="Description">{order.description || "N/A"}</Descriptions.Item>
        </Descriptions>
        
        {lineItems && lineItems.length > 0 && (
          <>
            <Divider />
            <h3>Line Items</h3>
            <Table 
              dataSource={lineItems.map((item, index) => ({ ...item, key: index }))} 
              columns={lineItemColumns} 
              pagination={false}
              bordered
            />
          </>
        )}
      </>
    );
  };
  
  // Render transaction summary
  const renderTransactionSummary = () => {
    const batch = details.batch || {};
    
    return (
      <Descriptions title="Transaction Summary" bordered column={2}>
        <Descriptions.Item label="Transaction ID">{details.transId || transaction.transId || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Reference ID">{details.refTransId || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Submit Time">{formatDate(details.submitTimeUTC || transaction.submitTimeUTC)}</Descriptions.Item>
        <Descriptions.Item label="Local Time">{formatDate(details.submitTimeLocal || transaction.submitTimeLocal)}</Descriptions.Item>
        <Descriptions.Item label="Transaction Type">{details.transactionType || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={
            details.transactionStatus === "settledSuccessfully" ? "green" : 
            details.transactionStatus === "declined" ? "red" : 
            details.transactionStatus === "voided" ? "orange" : 
            "blue"
          }>
            {details.transactionStatus || transaction.transactionStatus || "N/A"}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Response Code">{details.responseCode || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Response">{details.responseReasonDescription || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Auth Code">{details.authCode || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="AVS Response">{details.AVSResponse || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Requested Amount">{formatAmount(details.requestedAmount)}</Descriptions.Item>
        <Descriptions.Item label="Auth Amount">{formatAmount(details.authAmount)}</Descriptions.Item>
        <Descriptions.Item label="Settle Amount">{formatAmount(details.settleAmount || transaction.settleAmount)}</Descriptions.Item>
        <Descriptions.Item label="Tax Amount">{formatAmount(details.tax?.amount)}</Descriptions.Item>
        <Descriptions.Item label="Shipping Amount">{formatAmount(details.shipping?.amount)}</Descriptions.Item>
        <Descriptions.Item label="Duty Amount">{formatAmount(details.duty?.amount)}</Descriptions.Item>
      </Descriptions>
    );
  };
  
  // Render batch information
  const renderBatchInfo = () => {
    const batch = details.batch || {};
    
    if (!batch || !batch.batchId) {
      return (
        <Descriptions title="Batch Information" bordered>
          <Descriptions.Item span={3}>
            <Tag color="orange">Unsettled Transaction</Tag>
          </Descriptions.Item>
        </Descriptions>
      );
    }
    
    return (
      <Descriptions title="Batch Information" bordered column={2}>
        <Descriptions.Item label="Batch ID">{batch.batchId || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Settlement State">{batch.settlementState || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Settlement Time (UTC)">{formatDate(batch.settlementTimeUTC)}</Descriptions.Item>
        <Descriptions.Item label="Settlement Time (Local)">{formatDate(batch.settlementTimeLocal)}</Descriptions.Item>
      </Descriptions>
    );
  };

  return (
    <div className="transaction-details">
      <div className="card">
        <div className="card-body">
          <h3 className="card-title mb-4">Transaction Details</h3>
          
          {renderTransactionSummary()}
          
          <Divider />
          
          {renderBatchInfo()}
          
          <Divider />
          
          {renderPaymentInfo()}
          
          <Divider />
          
          {renderCustomerInfo()}
          
          <Divider />
          
          {renderOrderInfo()}
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;
